import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { observer, inject } from 'mobx-react';
import ButtonWithLoading from '../form-fields/ButtonWithLoading';
import CloseOneIcon from '../svg/CloseOneIcon';

const SaveCartModal = inject('wooCommerceStore', 'studioStore')(observer(({
  isOpen,
  onClose,
  wooCommerceStore,
  studioStore,
}) => {
  const t = useTranslations('Cart');
  const tg = useTranslations('General');
  const [cartName, setCartName] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!isOpen) return null;

  const container = studioStore.settings.product_popup?.container;
  const productInfoBackgroundColor = studioStore.settings.product_popup?.product_info_background_color || 'white';
  const primaryColor = studioStore.settings.colors.primary_color;

  let classes = "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md md:mx-auto max-h-[90vh] z-[60] overscroll-contain";
  const style = {
    backgroundColor: productInfoBackgroundColor,
  };

  if (container === "stamp") {
    classes = classes + " stamp-border";
    style.backgroundImage = `radial-gradient(8px, transparent 98%, ${productInfoBackgroundColor}),linear-gradient(${productInfoBackgroundColor} 0px, ${productInfoBackgroundColor} 0px)`;
    style.backgroundColor = '';
  } else {
    classes = classes + " rounded-lg shadow-lg p-4 bg-white";
  }

  const handleSaveCart = async (e) => {
    e.preventDefault();

    if (!cartName.trim()) {
      setError(t('cart_name_required'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await wooCommerceStore.saveCart(cartName, isShared);
      setSuccess(t('cart_saved_successfully'));
      setCartName('');
      setIsShared(false);

      // Close modal after a delay
      setTimeout(() => {
        onClose();
        setSuccess('');
      }, 2000);

    } catch (err) {
      setError(err.message || t('failed_to_save_cart'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="fixed inset-0 transition-opacity" aria-hidden="true">
        <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <div className={classes} style={style}>
        <button
          className="absolute top-4 md:-top-8 left-4 md:-left-10 rtl:right-4 rtl:md:-right-10 rtl:left-auto z-10"
          onClick={onClose}
          disabled={loading}
        >
          <CloseOneIcon size="28px" stroke={primaryColor} />
        </button>

        <div className="max-h-[86vh] overflow-y-auto">
          <div className="p-3 md:p-6">
            <div className="px-4 md:px-8">
              <h2 className="text-2xl font-semibold mb-6 text-center" style={{color: primaryColor}}>
                {t('save_cart')}
              </h2>

              {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                  {error}
                </div>
              )}

              {success && (
                <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md">
                  {success}
                </div>
              )}

              <form onSubmit={handleSaveCart}>
                <div className="mb-4">
                  <label htmlFor="cartName" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('cart_name')}
                  </label>
                  <input
                    type="text"
                    id="cartName"
                    value={cartName}
                    onChange={(e) => setCartName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-studio focus:border-primary-studio"
                    placeholder={t('enter_cart_name')}
                    disabled={loading}
                  />
                </div>

                <div className="mb-4">
                  <label className="flex items-center text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={isShared}
                      onChange={(e) => setIsShared(e.target.checked)}
                      className="mr-2 h-4 w-4 text-primary-studio rounded border-gray-300 focus:ring-primary-studio"
                      disabled={loading}
                    />
                    {t('make_cart_shareable')}
                  </label>
                </div>

                <div className="flex justify-center space-x-3 mt-6">
                  <ButtonWithLoading
                    type="button"
                    onClick={onClose}
                    text={tg('cancel')}
                    outline
                    disabled={loading}
                    className="px-4 py-2"
                  />

                  <ButtonWithLoading
                    type="submit"
                    text={t('save')}
                    loading={loading}
                    className="px-4 py-2"
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}));

export default SaveCartModal;